import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Eye, Layers3, Download, CheckCircle2, Clock, Trash2, Printer } from "lucide-react";
import { usePdfPreview } from "../components/PdfPreview";

const CONS_ROLES = ["provincial_supervisor", "admin"];

export default function ConsolidatedReports() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const today = new Date();
  const [items, setItems] = useState([]);
  const [cand, setCand] = useState(null);
  const [sel, setSel] = useState([]);
  const [year, setYear] = useState(today.getFullYear());
  const [week, setWeek] = useState("");
  const [province, setProvince] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const pdf = usePdfPreview();

  const canBuild = CONS_ROLES.includes(user?.role);

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await api.get("/harmonized/consolidation");
      setItems(res.data);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const loadCandidates = async () => {
    if (!canBuild) return;
    setBusy("cand");
    try {
      const params = { year };
      if (week) params.week = Number(week);
      if (province) params.province = province;
      const res = await api.get("/harmonized/consolidation/candidates", { params });
      setCand(res.data);
      setWeek(String(res.data.week));
      setProvince(res.data.province || "");
      setSel(res.data.candidates.map((c) => c.id));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  useEffect(() => { loadList(); loadCandidates(); /* eslint-disable-next-line */ }, []);

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const build = async () => {
    setBusy("build");
    try {
      const res = await api.post("/harmonized/consolidation", {
        report_ids: sel, province: cand?.province,
      });
      toast.success(t("Rapport provincial consolidé généré."));
      navigate(`/consolidated/${res.data.id}`);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const downloadPdf = async (r) => {
    try {
      const res = await api.get(`/harmonized/consolidation/${r.id}/pdf`, { responseType: "blob", params: { lang } });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Consolide_${r.province}_${r.period_key}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const previewPdf = (r) => pdf.show(
    () => api.get(`/harmonized/consolidation/${r.id}/pdf`, { responseType: "blob", params: { lang } }).then((x) => x.data),
    { title: `${r.title || r.period_label} · ${r.province}`, filename: `Consolide_${r.province}_${r.period_key}.pdf` },
  ).catch((err) => toast.error(apiErr(err.response?.data?.detail)));

  const remove = async (r) => {
    if (!window.confirm(t("Supprimer ce rapport consolidé ?"))) return;
    try {
      await api.delete(`/harmonized/consolidation/${r.id}`);
      await loadList();
      toast.success(t("Rapport consolidé supprimé."));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="consolidated-page">
      <PageHeader eyebrow={t("Coordination provinciale")} title={t("Rapport Consolidé")}
        subtitle={t("Fusionnez les rapports hebdomadaires validés de votre province en un rapport provincial unique.")}
        testid="consolidated-header" />

      {canBuild && (
        <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5 mb-5" data-testid="cons-builder">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">{t("Nouvelle consolidation")}</h2>
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div className="w-28">
              <Label className="text-xs">{t("Année")}</Label>
              <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="cons-year" />
            </div>
            <div className="w-28">
              <Label className="text-xs">{t("Semaine")}</Label>
              <Input type="number" min={1} max={53} value={week} onChange={(e) => setWeek(e.target.value)} data-testid="cons-week" />
            </div>
            {user?.role === "admin" && (
              <div className="w-56">
                <Label className="text-xs">{t("Province")}</Label>
                <Input value={province} onChange={(e) => setProvince(e.target.value)} placeholder="Nord-Kivu" data-testid="cons-province" />
              </div>
            )}
            <Button variant="outline" className="rounded-md" onClick={loadCandidates} disabled={!!busy} data-testid="cons-load">
              {busy === "cand" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Clock className="h-4 w-4 mr-1.5" />}
              {t("Charger les rapports validés")}
            </Button>
          </div>

          {cand && (cand.candidates.length === 0 ? (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="cons-no-candidates">
              {t("Aucun rapport hebdomadaire validé pour cette semaine dans")} {cand.province}.
            </p>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-2">
                {cand.province} · {cand.period_key} · {cand.candidates.length} {t("rapport(s) validé(s) disponible(s)")}
              </p>
              <div className="overflow-x-auto rounded-md border border-slate-200 mb-4">
                <table className="w-full text-sm">
                  <thead><tr className="bg-[#F1F5F9] text-left">
                    {["", "Auteur", "Zone de santé", "Pilier", "Saisies", "Validé par"].map((h, i) => (
                      <th key={i} className="p-2.5 text-xs font-bold text-slate-600">{t(h)}</th>))}
                  </tr></thead>
                  <tbody>
                    {cand.candidates.map((c) => (
                      <tr key={c.id} className="border-t border-slate-100" data-testid={`cons-cand-${c.id}`}>
                        <td className="p-2.5">
                          <input type="checkbox" checked={sel.includes(c.id)} onChange={() => toggle(c.id)}
                            className="h-4 w-4 accent-[#F05A28]" data-testid={`cons-check-${c.id}`} />
                        </td>
                        <td className="p-2.5 font-medium text-slate-800">{c.author_name}</td>
                        <td className="p-2.5 text-slate-600">{c.health_zone || "-"}</td>
                        <td className="p-2.5 text-slate-600">{c.pillar_label}</td>
                        <td className="p-2.5 text-slate-600">{c.entries_count}</td>
                        <td className="p-2.5 text-slate-600">{c.signed_by || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button onClick={build} disabled={!!busy || sel.length === 0}
                className="rounded-md bg-[#F05A28] hover:bg-[#D84A1B]" data-testid="cons-build-btn">
                {busy === "build" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Layers3 className="h-4 w-4 mr-1.5" />}
                {t("Consolider la sélection")} ({sel.length})
              </Button>
            </>
          ))}
        </section>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#F05A28]" /></div>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-400" data-testid="cons-empty">{t("Aucun rapport consolidé pour le moment.")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#F1F5F9] text-left">
                {["Province", "Période", "Rapports", "Saisies", "Statut", ""].map((h, i) => (
                  <th key={i} className="p-3 text-xs font-bold text-slate-600">{t(h)}</th>))}
              </tr></thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-[#F8FAFC]" data-testid={`cons-row-${r.id}`}>
                    <td className="p-3 font-medium text-slate-800">{r.province}</td>
                    <td className="p-3 text-slate-600">{r.period_label || r.period_key}</td>
                    <td className="p-3 text-slate-600">{r.reports_count}</td>
                    <td className="p-3 text-slate-600">{r.entries_count}</td>
                    <td className="p-3">
                      {r.status === "signed" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />{t("Validé")}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500"><Clock className="h-3.5 w-3.5" />{t("Brouillon")}</span>
                      )}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => previewPdf(r)} data-testid={`cons-preview-${r.id}`} title={t("Aperçu avant impression")}>
                        <Printer className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(r)} data-testid={`cons-pdf-${r.id}`} title={t("Télécharger PDF")}>
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/consolidated/${r.id}`)} data-testid={`cons-view-${r.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canBuild && (
                        <Button size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`cons-del-${r.id}`}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {pdf.el}
    </div>
  );
}
